import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Trash2, Receipt, X, ChevronDown, ChevronUp, Copy, Check } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import AutocompleteProducto from '../components/AutocompleteProducto';
import { formatMonto, formatFecha, hoyCaracas } from '../utils/format';
import BotonAyuda from '../components/BotonAyuda';
import Exportar from '../components/reportes/Exportar';
import { construirCanvasTabla, copiarOCaerADescarga } from '../utils/canvasTabla';

// Detalle de un vendedor (#153): matriz de comisión (producto × tipo_precio),
// clientes asignados y liquidaciones de comisión (a pedido, sin período fijo).
const TIPOS_PRECIO = ['detal', 'mayor', 'fabrica'];
const FORM_REGLA_VACIO = { producto_id: null, tipo_precio: 'detal', modo: 'porcentaje', valor: '', vigente_desde: hoyCaracas() };

export default function VendedorDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { empresaActiva, empresas } = useAuth();
  const empresaNombre = empresas.find((e) => String(e.empresa_id) === String(empresaActiva))?.empresa_nombre;

  const [pestana, setPestana] = useState('matriz');
  const [vendedor, setVendedor] = useState(null);

  const [productos, setProductos] = useState([]);
  const [reglas, setReglas] = useState([]);
  const [formRegla, setFormRegla] = useState(FORM_REGLA_VACIO);
  const [errorRegla, setErrorRegla] = useState('');

  const [clientes, setClientes] = useState([]);
  const [buscarCliente, setBuscarCliente] = useState('');

  const [pendiente, setPendiente] = useState({ lineas: [], sin_regla: [], total: 0 });
  const [liquidaciones, setLiquidaciones] = useState([]);
  const [liquidacionAbierta, setLiquidacionAbierta] = useState(null);
  const [detalleLiquidacion, setDetalleLiquidacion] = useState(null);
  const [generando, setGenerando] = useState(false);
  const [imgCopiadaPendiente, setImgCopiadaPendiente] = useState(false);
  const [imgCopiadaLiquidaciones, setImgCopiadaLiquidaciones] = useState(false);

  async function cargarVendedor() {
    const { data } = await api.get('/vendedores');
    setVendedor(data.find((v) => String(v.id) === String(id)) || null);
  }

  async function cargarReglas() {
    const { data } = await api.get(`/vendedores/${id}/reglas`);
    setReglas(data);
  }

  async function cargarClientes() {
    const { data } = await api.get('/clientes');
    setClientes(data);
  }

  async function cargarPendiente() {
    const { data } = await api.get(`/vendedores/${id}/comisiones/pendiente`);
    setPendiente(data);
  }

  async function cargarLiquidaciones() {
    const { data } = await api.get(`/vendedores/${id}/comisiones/liquidaciones`);
    setLiquidaciones(data);
  }

  useEffect(() => {
    if (!empresaActiva) return;
    cargarVendedor();
    api.get('/productos').then(({ data }) => setProductos(data));
    cargarReglas();
    cargarClientes();
    cargarPendiente();
    cargarLiquidaciones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActiva, id]);

  // ---------- Matriz de comisión ----------

  async function guardarRegla(e) {
    e.preventDefault();
    setErrorRegla('');
    if (!formRegla.producto_id || !formRegla.valor) return;
    try {
      await api.post(`/vendedores/${id}/reglas`, formRegla);
      setFormRegla(FORM_REGLA_VACIO);
      cargarReglas();
      cargarPendiente();
    } catch (err) {
      setErrorRegla(err.response?.data?.error || 'No se pudo guardar la regla');
    }
  }

  async function eliminarRegla(reglaId) {
    if (!window.confirm('¿Eliminar esta regla de comisión?')) return;
    try {
      await api.delete(`/vendedores/${id}/reglas/${reglaId}`);
      cargarReglas();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo eliminar la regla');
    }
  }

  // ---------- Clientes asignados ----------

  const clientesAsignados = clientes.filter((c) => String(c.vendedor_id) === String(id));
  const q = buscarCliente.trim().toLowerCase();
  const candidatosCliente = q
    ? clientes.filter((c) => String(c.vendedor_id) !== String(id) && c.nombre.toLowerCase().includes(q)).slice(0, 8)
    : [];

  async function asignarCliente(cliente) {
    try {
      await api.put(`/clientes/${cliente.id}`, { vendedor_id: id });
      setBuscarCliente('');
      cargarClientes();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo asignar el cliente');
    }
  }

  async function quitarCliente(cliente) {
    if (!window.confirm(`¿Quitar a "${cliente.nombre}" de este vendedor?`)) return;
    try {
      await api.put(`/clientes/${cliente.id}`, { vendedor_id: null });
      cargarClientes();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo quitar el cliente');
    }
  }

  // ---------- Liquidaciones ----------

  async function generarLiquidacion() {
    if (!window.confirm(`¿Generar la liquidación por ${formatMonto(pendiente.total)}? Esas ventas quedan marcadas como liquidadas.`)) return;
    setGenerando(true);
    try {
      await api.post(`/vendedores/${id}/comisiones/liquidaciones`, {});
      await Promise.all([cargarPendiente(), cargarLiquidaciones()]);
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo generar la liquidación');
    } finally {
      setGenerando(false);
    }
  }

  async function verDetalle(liq) {
    if (liquidacionAbierta === liq.id) {
      setLiquidacionAbierta(null);
      return;
    }
    const { data } = await api.get(`/vendedores/${id}/comisiones/liquidaciones/${liq.id}`);
    setDetalleLiquidacion(data);
    setLiquidacionAbierta(liq.id);
  }

  async function marcarPagada(liq) {
    if (!window.confirm(`¿Marcar la liquidación de ${formatMonto(liq.monto_total)} del ${formatFecha(liq.fecha)} como pagada?`)) return;
    try {
      await api.put(`/vendedores/${id}/comisiones/liquidaciones/${liq.id}/pagar`, {});
      cargarLiquidaciones();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo marcar como pagada');
    }
  }

  // ---------- Descargar / copiar (§14.1 y §6.5 de docs/ESTANDAR-UI.md) ----------

  function excelPendiente() {
    return {
      titulo: `Comisión pendiente - ${vendedor.nombre}`,
      detalle: 'Ventas pagadas todavía no liquidadas',
      hojas: [{
        nombre: 'Pendiente',
        columnas: [
          { titulo: 'Factura', tipo: 'texto' },
          { titulo: 'Fecha', tipo: 'fecha' },
          { titulo: 'Cliente', tipo: 'texto' },
          { titulo: 'Producto', tipo: 'texto' },
          { titulo: 'Tipo de precio', tipo: 'texto' },
          { titulo: 'Comisión', tipo: 'monto' }
        ],
        filas: pendiente.lineas.map((l) => [`#${l.numero}`, l.fecha, l.cliente_nombre, l.producto_nombre, l.tipo_precio_inferido, l.monto_comision]),
        total: ['Total', '', '', '', '', pendiente.total]
      }]
    };
  }

  async function copiarImagenPendiente() {
    try {
      const canvas = construirCanvasTabla({
        titulo: `Comisión pendiente de liquidar`,
        subtitulo: [vendedor.nombre, empresaNombre, formatFecha(hoyCaracas())].filter(Boolean).join(' · '),
        columnas: [
          { label: 'Factura' }, { label: 'Fecha' }, { label: 'Cliente' }, { label: 'Producto' },
          { label: 'Tipo precio' }, { label: 'Comisión', align: 'right' }
        ],
        filas: pendiente.lineas.map((l) => [`#${l.numero}`, formatFecha(l.fecha), l.cliente_nombre, l.producto_nombre, l.tipo_precio_inferido, `$${formatMonto(l.monto_comision)}`]),
        pie: `Total: $${formatMonto(pendiente.total)}`
      });
      const copiado = await copiarOCaerADescarga(canvas, `comision-pendiente-${vendedor.nombre}.png`);
      if (copiado) {
        setImgCopiadaPendiente(true);
        setTimeout(() => setImgCopiadaPendiente(false), 1500);
      } else {
        alert('Tu navegador no permite copiar imágenes; se descargó el archivo.');
      }
    } catch (err) {
      alert('No se pudo generar la imagen');
    }
  }

  function excelLiquidaciones() {
    return {
      titulo: `Liquidaciones - ${vendedor.nombre}`,
      detalle: 'Historial de liquidaciones de comisión',
      hojas: [{
        nombre: 'Liquidaciones',
        columnas: [
          { titulo: 'Fecha', tipo: 'fecha' },
          { titulo: 'Monto', tipo: 'monto' },
          { titulo: 'Líneas', tipo: 'entero' },
          { titulo: 'Estado', tipo: 'texto' },
          { titulo: 'Fecha de pago', tipo: 'fecha' }
        ],
        filas: liquidaciones.map((liq) => [liq.fecha, liq.monto_total, liq.lineas, liq.estado === 'pagada' ? 'Pagada' : 'Pendiente de pago', liq.fecha_pago || ''])
      }]
    };
  }

  async function copiarImagenLiquidaciones() {
    try {
      const canvas = construirCanvasTabla({
        titulo: 'Historial de liquidaciones',
        subtitulo: [vendedor.nombre, empresaNombre, formatFecha(hoyCaracas())].filter(Boolean).join(' · '),
        columnas: [
          { label: 'Fecha' }, { label: 'Monto', align: 'right' }, { label: 'Líneas', align: 'right' }, { label: 'Estado' }
        ],
        filas: liquidaciones.map((liq) => [
          formatFecha(liq.fecha),
          `$${formatMonto(liq.monto_total)}`,
          String(liq.lineas),
          liq.estado === 'pagada' ? `Pagada · ${formatFecha(liq.fecha_pago)}` : 'Pendiente de pago'
        ])
      });
      const copiado = await copiarOCaerADescarga(canvas, `liquidaciones-${vendedor.nombre}.png`);
      if (copiado) {
        setImgCopiadaLiquidaciones(true);
        setTimeout(() => setImgCopiadaLiquidaciones(false), 1500);
      } else {
        alert('Tu navegador no permite copiar imágenes; se descargó el archivo.');
      }
    } catch (err) {
      alert('No se pudo generar la imagen');
    }
  }

  if (!vendedor) {
    return (
      <div className="p-4">
        <button onClick={() => navigate('/vendedores')} className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1">
          <ArrowLeft size={16} /> Volver a Vendedores
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/vendedores')} title="Volver a Vendedores" className="text-slate-400 hover:text-slate-700">
          <ArrowLeft size={18} />
        </button>
        <h1 className="text-2xl font-bold text-slate-800">
          {vendedor.nombre}
          {empresaNombre && <span className="text-slate-400 font-normal"> · {empresaNombre}</span>}
          <BotonAyuda />
        </h1>
      </div>

      <div className="flex gap-1 border-b border-slate-200">
        {[['matriz', 'Matriz de comisión'], ['clientes', 'Clientes asignados'], ['liquidaciones', 'Liquidaciones']].map(([k, t]) => (
          <button
            key={k}
            type="button"
            onClick={() => setPestana(k)}
            className={`px-3.5 py-2.5 text-sm ${pestana === k ? 'font-semibold text-primary-600 border-b-2 border-primary-600' : 'text-slate-500 hover:text-slate-700'}`}
          >
            {t}
          </button>
        ))}
      </div>

      {pestana === 'matriz' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl shadow-sm p-4">
            <h2 className="text-sm font-semibold text-slate-700 mb-3">Agregar regla</h2>
            <form onSubmit={guardarRegla} className="grid grid-cols-1 sm:grid-cols-5 gap-3 items-end">
              <div className="sm:col-span-2">
                <label className="block text-xs text-slate-500 mb-1">Producto</label>
                <AutocompleteProducto
                  productos={productos}
                  value={formRegla.producto_id}
                  onChange={(producto_id) => setFormRegla({ ...formRegla, producto_id })}
                  placeholder="Buscar producto..."
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Tipo de precio</label>
                <select
                  value={formRegla.tipo_precio}
                  onChange={(e) => setFormRegla({ ...formRegla, tipo_precio: e.target.value })}
                  className="border rounded-md px-3 py-2 w-full capitalize"
                >
                  {TIPOS_PRECIO.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Modo</label>
                <select
                  value={formRegla.modo}
                  onChange={(e) => setFormRegla({ ...formRegla, modo: e.target.value })}
                  className="border rounded-md px-3 py-2 w-full"
                >
                  <option value="porcentaje">% Porcentaje</option>
                  <option value="fijo">Monto fijo (por unidad)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">{formRegla.modo === 'porcentaje' ? 'Valor (%)' : 'Valor ($ por unidad)'}</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={formRegla.valor}
                  onChange={(e) => setFormRegla({ ...formRegla, valor: e.target.value })}
                  className="border rounded-md px-3 py-2 w-full text-right"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs text-slate-500 mb-1">Vigente desde</label>
                <input
                  type="date"
                  required
                  value={formRegla.vigente_desde}
                  onChange={(e) => setFormRegla({ ...formRegla, vigente_desde: e.target.value })}
                  className="border rounded-md px-3 py-2 w-full"
                />
              </div>
              <button type="submit" className="bg-primary-600 text-white rounded-md py-2 px-4 hover:bg-primary-700 sm:col-span-1">
                Agregar
              </button>
              {errorRegla && <p className="text-red-600 text-sm sm:col-span-5">{errorRegla}</p>}
            </form>
          </div>

          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-left">
                  <tr>
                    <th className="p-3">Producto</th>
                    <th className="p-3">Tipo de precio</th>
                    <th className="p-3">Modo</th>
                    <th className="p-3 text-right">Valor</th>
                    <th className="p-3">Vigente desde</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {reglas.map((r) => (
                    <tr key={r.id} className="border-t hover:bg-slate-50 transition-colors">
                      <td className="p-3 font-medium whitespace-nowrap">{r.producto_nombre}</td>
                      <td className="p-3 capitalize whitespace-nowrap">{r.tipo_precio}</td>
                      <td className="p-3 whitespace-nowrap">{r.modo === 'porcentaje' ? 'Porcentaje' : 'Monto fijo'}</td>
                      <td className="p-3 text-right whitespace-nowrap">{r.modo === 'porcentaje' ? `${formatMonto(r.valor)}%` : `$${formatMonto(r.valor)}`}</td>
                      <td className="p-3 whitespace-nowrap">{formatFecha(r.vigente_desde)}</td>
                      <td className="p-3">
                        <div className="flex items-center gap-3 justify-end">
                          <button onClick={() => eliminarRegla(r.id)} title="Eliminar" className="text-slate-400 hover:text-red-600 transition-colors">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {reglas.length === 0 && (
                    <tr>
                      <td colSpan={6} className="p-6 text-center text-slate-400">
                        Este vendedor todavía no tiene reglas de comisión.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {pestana === 'clientes' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl shadow-sm p-4">
            <label className="block text-xs text-slate-500 mb-1">Asignar cliente a {vendedor.nombre}</label>
            <div className="relative max-w-sm">
              <input
                value={buscarCliente}
                onChange={(e) => setBuscarCliente(e.target.value)}
                placeholder="Buscar cliente por nombre..."
                className="border rounded-md px-3 py-2 w-full text-sm"
              />
              {candidatosCliente.length > 0 && (
                <ul className="absolute z-10 mt-1 w-full bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto text-sm">
                  {candidatosCliente.map((c) => (
                    <li
                      key={c.id}
                      onClick={() => asignarCliente(c)}
                      className="px-3 py-1.5 cursor-pointer hover:bg-primary-50"
                    >
                      {c.nombre}{c.vendedor_nombre ? <span className="text-slate-400"> · hoy con {c.vendedor_nombre}</span> : ''}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-left">
                  <tr>
                    <th className="p-3">Cliente</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {clientesAsignados.map((c) => (
                    <tr key={c.id} className="border-t hover:bg-slate-50 transition-colors">
                      <td className="p-3 font-medium whitespace-nowrap">{c.nombre}</td>
                      <td className="p-3">
                        <div className="flex items-center justify-end">
                          <button onClick={() => quitarCliente(c)} title="Quitar de este vendedor" className="text-slate-400 hover:text-red-600 transition-colors">
                            <X size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {clientesAsignados.length === 0 && (
                    <tr>
                      <td colSpan={2} className="p-6 text-center text-slate-400">
                        Este vendedor todavía no tiene clientes asignados.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {pestana === 'liquidaciones' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h2 className="text-sm font-semibold text-slate-700">Comisión pendiente de liquidar</h2>
              <div className="flex items-center gap-2">
                {pendiente.lineas.length > 0 && (
                  <>
                    <button
                      onClick={copiarImagenPendiente}
                      title="Copiar como imagen"
                      className="text-slate-400 hover:text-primary-600 transition-colors"
                    >
                      {imgCopiadaPendiente ? <Check size={16} className="text-emerald-600" /> : <Copy size={16} />}
                    </button>
                    <Exportar excel={excelPendiente} pdf={false} />
                  </>
                )}
                <button
                  onClick={generarLiquidacion}
                  disabled={generando || pendiente.lineas.length === 0}
                  title="Generar liquidación"
                  className="flex items-center gap-1.5 text-sm bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-md px-3 py-1.5"
                >
                  <Receipt size={16} /> {generando ? 'Generando…' : `Generar liquidación ($${formatMonto(pendiente.total)})`}
                </button>
              </div>
            </div>

            {pendiente.lineas.length === 0 && pendiente.sin_regla.length === 0 && (
              <p className="text-sm text-slate-400">No hay ventas pagadas pendientes de liquidar.</p>
            )}

            {pendiente.lineas.length > 0 && (
              <div className="overflow-x-auto border rounded-lg mb-3">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-left">
                    <tr>
                      <th className="p-2">Factura</th>
                      <th className="p-2">Fecha</th>
                      <th className="p-2">Cliente</th>
                      <th className="p-2">Producto</th>
                      <th className="p-2 capitalize">Tipo precio</th>
                      <th className="p-2 text-right">Comisión</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendiente.lineas.map((l) => (
                      <tr key={l.factura_linea_id} className="border-t">
                        <td className="p-2 whitespace-nowrap">#{l.numero}</td>
                        <td className="p-2 whitespace-nowrap">{formatFecha(l.fecha)}</td>
                        <td className="p-2 whitespace-nowrap">{l.cliente_nombre}</td>
                        <td className="p-2 whitespace-nowrap">{l.producto_nombre}</td>
                        <td className="p-2 capitalize whitespace-nowrap">{l.tipo_precio_inferido}</td>
                        <td className="p-2 text-right whitespace-nowrap">${formatMonto(l.monto_comision)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {pendiente.sin_regla.length > 0 && (
              <p className="text-xs text-amber-700 bg-amber-50 rounded-md p-2">
                {pendiente.sin_regla.length} venta(s) pagada(s) sin una regla de comisión configurada para ese producto/tipo de precio
                todavía no cuentan: {[...new Set(pendiente.sin_regla.map((l) => `${l.producto_nombre} (${l.tipo_precio_inferido})`))].join(', ')}.
              </p>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-4 pt-4 flex-wrap gap-2">
              <h2 className="text-sm font-semibold text-slate-700">Historial de liquidaciones</h2>
              {liquidaciones.length > 0 && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={copiarImagenLiquidaciones}
                    title="Copiar como imagen"
                    className="text-slate-400 hover:text-primary-600 transition-colors"
                  >
                    {imgCopiadaLiquidaciones ? <Check size={16} className="text-emerald-600" /> : <Copy size={16} />}
                  </button>
                  <Exportar excel={excelLiquidaciones} pdf={false} />
                </div>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-left">
                  <tr>
                    <th className="p-3">Fecha</th>
                    <th className="p-3 text-right">Monto</th>
                    <th className="p-3 text-right">Líneas</th>
                    <th className="p-3">Estado</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {liquidaciones.map((liq) => (
                    <React.Fragment key={liq.id}>
                      <tr onClick={() => verDetalle(liq)} className="border-t hover:bg-slate-50 transition-colors cursor-pointer">
                        <td className="p-3 whitespace-nowrap flex items-center gap-1.5">
                          {liquidacionAbierta === liq.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                          {formatFecha(liq.fecha)}
                        </td>
                        <td className="p-3 text-right whitespace-nowrap">${formatMonto(liq.monto_total)}</td>
                        <td className="p-3 text-right">{liq.lineas}</td>
                        <td className="p-3">
                          <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${liq.estado === 'pagada' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                            {liq.estado === 'pagada' ? `Pagada${liq.fecha_pago ? ' · ' + formatFecha(liq.fecha_pago) : ''}` : 'Pendiente de pago'}
                          </span>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center justify-end" onClick={(e) => e.stopPropagation()}>
                            {liq.estado !== 'pagada' && (
                              <button onClick={() => marcarPagada(liq)} title="Marcar como pagada" className="text-xs border border-slate-300 rounded-md px-2 py-1 text-slate-700 hover:bg-slate-50">
                                Marcar pagada
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {liquidacionAbierta === liq.id && detalleLiquidacion && (
                        <tr className="border-t bg-slate-50">
                          <td colSpan={5} className="p-3">
                            <table className="w-full text-xs">
                              <thead className="text-slate-500 text-left">
                                <tr>
                                  <th className="py-1 pr-3">Factura</th>
                                  <th className="py-1 pr-3">Fecha</th>
                                  <th className="py-1 pr-3">Producto</th>
                                  <th className="py-1 pr-3 capitalize">Tipo precio</th>
                                  <th className="py-1 pr-3 text-right">Comisión</th>
                                </tr>
                              </thead>
                              <tbody>
                                {detalleLiquidacion.detalle.map((d) => (
                                  <tr key={d.id} className="border-t border-slate-200">
                                    <td className="py-1 pr-3 whitespace-nowrap">#{d.numero}</td>
                                    <td className="py-1 pr-3 whitespace-nowrap">{formatFecha(d.fecha)}</td>
                                    <td className="py-1 pr-3 whitespace-nowrap">{d.producto_nombre}</td>
                                    <td className="py-1 pr-3 capitalize whitespace-nowrap">{d.tipo_precio_inferido}</td>
                                    <td className="py-1 pr-3 text-right whitespace-nowrap">${formatMonto(d.monto_comision)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                  {liquidaciones.length === 0 && (
                    <tr>
                      <td colSpan={5} className="p-6 text-center text-slate-400">
                        Todavía no se ha generado ninguna liquidación.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
